import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  DollarSign,
  Users,
  Edit,
  Trash2,
  Loader2,
  Building2,
  Briefcase,
  User,
  Hash,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { jobService } from '../services';
import { JobListing } from '../types';

export const JobDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobListing | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadJob(id);
    }
  }, [id]);

  const loadJob = async (jobId: string) => {
    try {
      setLoading(true);
      setError(null);
      const data = await jobService.getJobById(jobId);
      setJob(data);
    } catch (err: any) {
      setError(err.message || 'Failed to load job');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!job) return;
    if (window.confirm('Are you sure you want to delete this job?')) {
      try {
        await jobService.deleteJob(job.id);
        navigate('/jobs');
      } catch (err: any) {
        setError(err.message || 'Failed to delete job');
      }
    }
  };

  const getStatusVariant = (status: string): 'default' | 'secondary' | 'destructive' => {
    switch (status) {
      case 'OPEN':
        return 'default';
      case 'CLOSED':
        return 'secondary';
      case 'CANCELLED':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  if (loading) {
    return (
      <div className="flex h-96 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" onClick={() => navigate('/jobs')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Jobs
        </Button>
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error || 'Job not found'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/jobs')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">{job.title}</h1>
            <p className="text-muted-foreground">Job Listing Details</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge variant={getStatusVariant(job.status)}>{job.status}</Badge>
          <Button variant="outline">
            <Edit className="h-4 w-4 mr-2" />
            Edit
          </Button>
          <Button variant="destructive" onClick={handleDelete}>
            <Trash2 className="h-4 w-4 mr-2" />
            Delete
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4 text-destructive">
          <p className="font-medium">Error</p>
          <p className="text-sm">{error}</p>
        </div>
      )}

      {/* Job Information */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <User className="h-4 w-4" />
                Posted By
              </div>
              <p className="text-base">{job.createdBy?.fullName || '-'}</p>
              <p className="text-sm text-muted-foreground">{job.createdBy?.email || ''}</p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Building2 className="h-4 w-4" />
                Branch
              </div>
              <p className="text-base">{job.branch?.name || '-'}</p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <MapPin className="h-4 w-4" />
                Location
              </div>
              <p className="text-base">{job.location || '-'}</p>
              <p className="text-sm text-muted-foreground">{job.city?.name || ''}</p>
            </div>

            <Separator />

            <div>
              <div className="text-sm font-medium text-muted-foreground mb-1">Description</div>
              <p className="text-base whitespace-pre-wrap">{job.description}</p>
            </div>

            {job.categories && job.categories.length > 0 && (
              <>
                <Separator />
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">Categories</div>
                  <div className="flex flex-wrap gap-2">
                    {job.categories.map((category) => (
                      <Badge key={category.id} variant="outline">
                        {category.emoji} {category.name}
                      </Badge>
                    ))}
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Budget & Payment */}
        <Card>
          <CardHeader>
            <CardTitle>Budget & Payment Terms</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <DollarSign className="h-4 w-4" />
                Total Budget
              </div>
              <p className="text-2xl font-bold">
                {job.totalBudget?.toLocaleString()} {job.currency || 'USD'}
              </p>
            </div>

            {job.paymentTermMonths && (
              <>
                <Separator />
                <div>
                  <div className="text-sm font-medium text-muted-foreground mb-1">
                    Payment Terms
                  </div>
                  <p className="text-base">
                    {job.paymentTermMonths} {job.paymentTermUnit || 'months'}
                  </p>
                  {job.paymentTermDescription && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {job.paymentTermDescription}
                    </p>
                  )}
                </div>
              </>
            )}

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div>
                <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                  <Hash className="h-4 w-4" />
                  Unit
                </div>
                <p className="text-base">
                  {job.unit?.name || '-'} ({job.unit?.abbreviation || '-'})
                </p>
              </div>
              <div>
                <div className="text-sm font-medium text-muted-foreground mb-1">Quantity</div>
                <p className="text-base">{job.quantity || 0}</p>
              </div>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Job Period
              </div>
              <p className="text-base">
                {job.startDate ? new Date(job.startDate).toLocaleDateString() : '-'}
                {job.endDate && ` - ${new Date(job.endDate).toLocaleDateString()}`}
              </p>
            </div>

            <Separator />

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Users className="h-4 w-4" />
                Applications
              </div>
              <p className="text-base">{job.applicantCount || 0} applicants</p>
              {job.hasAcceptedApplicant && (
                <p className="text-sm text-muted-foreground mt-1">
                  Accepted: {job.acceptedApplicantName}
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Requirements & Benefits */}
      <div className="grid gap-6 md:grid-cols-2">
        {/* Requirements */}
        {job.requirements && job.requirements.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5" />
                Requirements
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {job.requirements.map((req, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-muted-foreground">•</span>
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Benefits */}
        {job.benefits && job.benefits.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Benefits
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {job.benefits.map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-muted-foreground">•</span>
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Metadata */}
      <Card>
        <CardHeader>
          <CardTitle>Job Metadata</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Created At
              </div>
              <p className="text-base">{new Date(job.createdAt).toLocaleString()}</p>
            </div>

            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground mb-1">
                <Calendar className="h-4 w-4" />
                Last Updated
              </div>
              <p className="text-base">{new Date(job.updatedAt).toLocaleString()}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default JobDetailPage;
